import { observable, computed, action } from 'mobx';
import path from 'path';
import objectPath from 'object-path';

import { AppRegistry, ModelRegistry, StoreRegistry } from 'core/registry';
import { App, ArrayApp, Model, routerStore } from 'core';

import { clientStore } from 'apps/client/app';
import { SpaceModel, SpaceApp } from 'apps/space/app';
import { FileModel, FileApp } from 'apps/file/app';
import { OneColumnLayout, SmallThreeColumnLayout } from 'apps/layout';

import { ProfileModel, ProfileApp } from 'apps/profile/app';

import { conf } from 'apps/repository/config.yaml';
import { getPath } from 'apps/repository/utils';

export class RepositoryItem {
  contributionFile;
  operation;
  constructor({ contributionFile, operation }) {
    this.contributionFile = contributionFile;
    this.operation = operation;
  }

  get reference() {
    if (this.operation && this.operation.opType !== 'DELETE') {
      return this.operation;
    }
    return this.contributionFile;
  }
}

export class ContributionFileModel extends Model {
  static registryName = 'ContributionFileModel';
  static ModelProps = {
    file: FileModel,
    origin: 'ContributionModel',
    // contribution: ContributionModel,
  };

  @observable origin = null;
  @observable file = null;
  @observable dirname = '';
  @observable filename = '';
  @observable isFolder = false;
  @observable lastUpdated = '';
  @observable contribution = null;
}
ModelRegistry.add(ContributionFileModel);

export class ContributionFileApp extends App {
  static registryName = 'ContributionFileApp';
  static AppProps = {
    file: FileApp,
    origin: 'ContributionApp',
    // contribution: ContributionApp,
  };

  static Model = ContributionFileModel;
  @observable.ref blob = null;
  @observable fileAsText = null;
  @observable fileAsArrayBuffer = null;
  @observable fileAsDataURL = null;
  @observable renaming = false;
  @observable newFilename = '';

  @computed get draftKey() {
    const keyElements = [this.origin.project.id, this.origin.id, this.path];
    if (clientStore.signedInProfile) {
      keyElements.unshift(clientStore.signedInProfile.username);
    }
    return keyElements.join('-');
  }

  @computed get route() {
    if (!(this.origin && this.origin.project)) return {};
    return {
      name: `${this.origin.project.route.name}.version.file`,
      params: {
        ...this.origin.route.params,
        basepath: this.path,
      },
    };
  }

  @computed get path() {
    return getPath(this.dirname, this.filename);
  }

  @computed get downloadPath() {
    return this.file && this.file.permalink;
  }

  @computed get headPath() {
    if (!(this.origin && this.origin.project && this.origin.project.path))
      return '';
    return [this.origin.project.path, 'file', this.path].join('/');
  }

  @computed get versionPath() {
    if (!(this.origin && this.origin.project && this.origin.project.path)) {
      return '';
    }
    return [
      this.origin.project.path,
      'v',
      this.origin.shortVersion,
      'file',
      this.path,
    ].join('/');
  }

  @action SetRenaming = visible => {
    this.renaming = visible;
    this.newFilename = this.filename;
    const FormStore = StoreRegistry.get('forms.FormStore');
    this.form = visible ? new FormStore(this, conf, 'renameForm') : null;
  };
}
AppRegistry.add(ContributionFileApp);

export class ContributionFilesApp extends ArrayApp {
  static registryName = 'ContributionFilesApp';
  static ElementApp = ContributionFileApp;
}
AppRegistry.add(ContributionFilesApp);

export class OperationModel extends Model {
  static registryName = 'OperationModel';
  static ModelProps = {
    file: FileModel,
    oldFile: FileModel,
    origin: 'ContributionModel',
  };

  @observable opType;
  @observable file;
  @observable oldFile;
  @observable path;
  @observable oldPath;
  @observable renaming = false;
  @observable newFilename = '';
  @observable origin = null;
  @observable project = null;
}
ModelRegistry.add(OperationModel);

export class OperationApp extends App {
  static registryName = 'OperationApp';
  static AppProps = {
    file: FileApp,
    origin: 'ContributionApp',
    project: 'ContributionApp',
  };

  static Model = OperationModel;
  @observable fileAsText = null;
  @observable fileAsArrayBuffer = null;
  @observable fileAsDataURL = null;

  @computed get domId() {
    const validId = this.id.toLowerCase().replace(/[\s\(\)=]/g, '_');
    return `operation-${validId}`;
  }

  @computed get dirname() {
    const dirname = path.dirname(this.path);
    return dirname === '.' ? '' : dirname;
  }

  @computed get filename() {
    return path.basename(this.path);
  }

  @computed get isFolder() {
    return !this.file;
  }

  @computed get downloadPath() {
    return this.file && this.file.permalink;
  }

  @action setBlob = blob => {
    this.blob = blob;
    return blob;
  };

  @action SetRenaming = visible => {
    this.renaming = visible;
    this.newFilename = this.filename;
    const FormStore = StoreRegistry.get('forms.FormStore');
    this.form = visible ? new FormStore(this, conf, 'renameForm') : null;
  };
}
AppRegistry.add(OperationApp);

export class OperationsApp extends ArrayApp {
  static registryName = 'OperationsApp';
  static ElementApp = OperationApp;
  @computed get fileOps() {
    return this.filter(op => !op.isFolder);
  }

  @computed get sortedOps() {
    return this.fileOps.sort((a, b) =>
      a.filepath.toLowerCase() > b.filepath.toLowerCase() ? 1 : -1,
    );
  }
}
AppRegistry.add(OperationsApp);

export class ContributionModel extends Model {
  static registryName = 'ContributionModel';
  static ModelProps = {
    files: [ContributionFileModel],
    operations: [OperationModel],
    creatorProfile: ProfileModel,
    content: 'ContentModel',
    project: 'ProjectModel',
    space: 'SpaceModel',
  };

  @observable title = '';
  @observable description = '';
  @observable status;
  @observable files = [];
  @observable dateCreated;
  @observable lastUpdated;
  @observable operations = [];
  @observable creatorProfile = null;
  @observable content = null;
  @observable version;
  @observable project = null;
  @observable space = null;
  @observable zipArchiveUrl = '';
}
ModelRegistry.add(ContributionModel);

export class ContributionApp extends App {
  static registryName = 'ContributionApp';
  static AppProps = {
    files: ContributionFilesApp,
    operations: OperationsApp,
    creatorProfile: ProfileApp,
    content: 'ContentApp',
    project: 'ProjectApp',
  };

  static Model = ContributionModel;

  @computed get route() {
    if (!(this.version && this.project)) return {};
    return {
      name: `${this.project.route.name}.contribution`,
      params: {
        ...this.project.route.params,
        contributionId: this.shortVersion,
      },
    };
  }

  @computed get shortVersion() {
    return this.version ? this.version.substring(0, 7) : '';
  }
}
AppRegistry.add(ContributionApp);

export class ConflictModel extends Model {
  static registryName = 'ConflictModel';
  static ModelProps = {
    local: FileModel,
    remote: FileModel,
    remoteContribution: ContributionModel,
  };

  @observable local;
  @observable localStatus = '';
  @observable remote;
  @observable remoteContribution;
  @observable remoteStatus = '';
  @observable path = '';
}
ModelRegistry.add(ConflictModel);

export class ConflictApp extends App {
  static registryName = 'ConflictApp';
  static AppProps = {
    local: FileApp,
    remote: FileApp,
    remoteContribution: ContributionApp,
  };

  @observable markedResolved = false;
  @action SetMarkedResolved = bool => {
    this.markedResolved = bool;
  };

  static Model = ConflictModel;
}
AppRegistry.add(ConflictApp);

export class ConflictsArrayApp extends ArrayApp {
  static registryName = 'ConflictsArrayApp';
  static ElementApp = ConflictApp;
}

export class RepositoryModel extends Model {
  static registryName = 'RepositoryModel';
  static ModelProps = {
    inSpace: SpaceModel,
    contribution: ContributionModel,
    contributionUpstream: ContributionModel,
    pendingOperations: [OperationModel],
    conflicts: [ConflictModel],
  };

  @observable id;
  @observable inSpace = null;
  @observable contribution = null;
  @observable contributionUpstream = null;
  @observable pendingOperations = [];
  @observable contributionCount = 0;
  @observable contributors = { totalCount: 0 };
  @observable conflicts = [];
  @computed get contributorCount() {
    return this.contributors.totalCount;
  }

  @computed get zipArchiveUrl() {
    return this.contributionUpstream && this.contributionUpstream.zipArchiveUrl;
  }
}
ModelRegistry.add(RepositoryModel);

export class RepositoryApp extends App {
  static registryName = 'RepositoryApp';
  static AppProps = {
    inSpace: SpaceApp,
    contribution: ContributionApp,
    contributionUpstream: ContributionApp,
    pendingOperations: OperationsApp,
    conflicts: ConflictsArrayApp,
  };

  static Model = RepositoryModel;

  @observable localOperations = {};

  @computed get hasOpenContribution() {
    return this.pendingOperations.length > 0;
  }

  @computed get contributionReady() {
    return !this.pendingOperations.find(
      operation =>
        operation.opType === 'WIP' ||
        (!operation.isFolder && !operation.file.completed),
    );
  }

  folderSort = (a, b) => {
    const fileA = a.operation || a.file;
    const fileB = b.operation || b.file;
    if (fileA.isFolder && !fileB.isFolder) {
      return -1;
    }
    if (!fileA.isFolder && fileB.isFolder) {
      return 1;
    }
    return 0;
  };

  @computed get atHead() {
    return !routerStore.params.get('contributionId');
  }

  relativePath = (fullPath, cwd) => {
    const relativePath = path.relative(cwd, fullPath);
    return !relativePath.startsWith('..') && relativePath;
  };

  @computed get cwdFiles() {
    const basepath = routerStore.params.get('basepath') || '';
    const { contribution, contributionUpstream, pendingOperations } = this;
    const currentContribution = this.atHead
      ? contributionUpstream
      : contribution;
    const cwdObject = {};
    if (currentContribution) {
      currentContribution.files.forEach(contributionFile => {
        if (contributionFile.dirname === basepath) {
          cwdObject[contributionFile.path] = { file: contributionFile };
        }
      });
    }
    if (pendingOperations) {
      pendingOperations.forEach(operation => {
        const operationPath = this.relativePath(operation.path, basepath);
        const operationOldPath =
          operation.oldPath && this.relativePath(operation.oldPath, basepath);
        const relativePath = operationOldPath || operationPath;
        if (operation.dirname === basepath) {
          const accessPath = operation.oldPath || operation.path;
          cwdObject[accessPath] = Object.assign(
            { operation },
            cwdObject[accessPath],
          );
        } else if (relativePath) {
          const opApp = new OperationApp();
          opApp.hasContent = true;
          const accessPath = [basepath, relativePath.split('/')[0]]
            .filter(e => !!e)
            .join('/');
          opApp.path = [basepath, operationPath.split('/')[0]]
            .filter(e => !!e)
            .join('/');
          opApp.file = basepath === operation.dirname && operation.file;
          const existingObject = cwdObject[accessPath];
          const existingOpType =
            objectPath.get(existingObject, 'operation.opType') ||
            operation.opType;
          opApp.opType =
            operation.opType !== existingOpType ? 'UPDATE' : operation.opType;
          if (existingObject) delete cwdObject[accessPath].operation;
          cwdObject[accessPath] = Object.assign(
            { operation: opApp },
            cwdObject[accessPath],
          );
        }
      });
    }
    return Object.values(cwdObject)
      .sort(this.folderSort)
      .map(
        ({ operation, file }) =>
          new RepositoryItem({ operation, contributionFile: file }),
      );
  }

  FindFileCWD(name) {
    const basepath = routerStore.params.get('basepath') || '';
    const repoObject = this.cwdFiles.find(item => {
      const { reference } = item;
      return reference?.dirname === basepath && reference?.filename === name;
    });
    return repoObject;
  }
}
AppRegistry.add(RepositoryApp);

export class ContributionArrayApp extends ArrayApp {
  static registryName = 'ContributionArrayApp';
  static ElementApp = ContributionApp;
  cardLayout = new OneColumnLayout();
}
AppRegistry.add(ContributionArrayApp);

export class ContributorsApp extends ArrayApp {
  static registryName = 'ContributorsApp';
  static ElementApp = ProfileApp;
  cardLayout = new SmallThreeColumnLayout();
}
